import React, { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link, useHistory } from "react-router-dom";
import { auth, registerWithEmailAndPassword, registerInWithGoogle } from "../firebase";
import Logo from "../assets/logo.png";

function Register() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [locationKeys, setLocationKeys] = useState([]);

  const [user, loading] = useAuthState(auth);
  const history = useHistory();

  useEffect(() => {
    return history.listen(location => {
      if (history.action === 'PUSH') {
        setLocationKeys([location.key]);
      }
      if (history.action === 'POP') {
        if (locationKeys[1] === location.key) {
          setLocationKeys(([_, ...keys]) => keys);
          setStep(s => s + 1);
        } else {
          setLocationKeys(keys => [location.key, ...keys]);
          setStep(s => s - 1);
        }
      }
    });
  }, [locationKeys]);

  useEffect(() => {
    if (loading) return;
    if (user) history.replace("/tables");
  }, [user, loading]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex' }}>

      {/* ── Left panel (branding) ── desktop only */}
      <div className="d-none d-lg-flex" style={{
        flex: '0 0 45%',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '3rem',
        background: 'linear-gradient(145deg, var(--sb-bg-elevated) 0%, var(--sb-bg-surface) 100%)',
        borderRight: '1px solid var(--sb-border)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: '-80px', left: '-80px',
          width: 320, height: 320,
          background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}>
          <img src={Logo} width={32} height={30} alt="StakeBits" />
          <span style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--sb-text-primary)' }}>StakeBits</span>
        </Link>

        <div>
          <h2 style={{ color: 'var(--sb-text-primary)', fontWeight: 800, fontSize: '2rem', letterSpacing: '-0.03em', marginBottom: '1rem', lineHeight: 1.2 }}>
            Start tracking.<br />Play smarter.
          </h2>
          <p style={{ color: 'var(--sb-text-secondary)', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '2rem', maxWidth: 320 }}>
            Join StakeBits and get real-time access to live Evolution roulette statistics.
          </p>

          {[
            { icon: '📊', label: 'Live spin history across all tables' },
            { icon: '🌡️', label: 'Hot &amp; cold number tracking' },
            { icon: '🎡', label: 'Tier &amp; sector breakdowns' },
          ].map(({ icon, label }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '1rem' }}>{icon}</span>
              <span style={{ color: 'var(--sb-text-secondary)', fontSize: '0.875rem' }} dangerouslySetInnerHTML={{ __html: label }} />
            </div>
          ))}
        </div>

        <p style={{ color: 'var(--sb-text-muted)', fontSize: '0.78rem' }}>© 2024 StakeBits</p>
      </div>

      {/* ── Right panel (form) ── */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '3rem 2rem',
        background: 'var(--sb-bg-base)',
      }}>
        <div style={{ width: '100%', maxWidth: 400 }}>
          <h2 style={{ color: 'var(--sb-text-primary)', fontWeight: 700, fontSize: '1.6rem', letterSpacing: '-0.02em', marginBottom: '0.4rem' }}>
            Create an account
          </h2>
          <p style={{ color: 'var(--sb-text-secondary)', fontSize: '0.9rem', marginBottom: '2rem' }}>
            Start tracking live roulette statistics today
          </p>

          <form onSubmit={e => { e.preventDefault(); registerWithEmailAndPassword(email.toLowerCase(), password, name, answers); }}>

            <div style={{ marginBottom: '1.1rem' }}>
              <label style={{ display: 'block', color: 'var(--sb-text-muted)', fontSize: '0.73rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>
                Name
              </label>
              <input type="text" className="form-control" placeholder="Your name" value={name} onChange={e => setName(e.target.value)} required />
            </div>

            <div style={{ marginBottom: '1.1rem' }}>
              <label style={{ display: 'block', color: 'var(--sb-text-muted)', fontSize: '0.73rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>
                Email
              </label>
              <input type="email" className="form-control" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} required />
            </div>

            <div style={{ marginBottom: '1.1rem' }}>
              <label style={{ display: 'block', color: 'var(--sb-text-muted)', fontSize: '0.73rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-control"
                  placeholder="Create a password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  style={{ paddingRight: '2.75rem' }}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{
                  position: 'absolute', top: '50%', right: '0.75rem', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--sb-text-muted)', padding: 0, fontSize: '0.85rem',
                }}>
                  <i className={`fas ${showPassword ? 'fa-eye' : 'fa-eye-slash'}`} />
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', marginBottom: '1.75rem' }}>
              <input type="checkbox" id="terms" checked={accepted} onChange={e => setAccepted(e.target.checked)}
                style={{ marginTop: '2px', accentColor: 'var(--sb-accent)', flexShrink: 0 }} />
              <label htmlFor="terms" style={{ color: 'var(--sb-text-secondary)', fontSize: '0.8rem', lineHeight: 1.5, cursor: 'pointer' }}>
                I accept the{' '}
                <a href="https://stakebits.co/terms" target="_blank" rel="noreferrer" style={{ color: 'var(--sb-accent)' }}>terms &amp; conditions</a>
                {' '}and{' '}
                <a href="https://stakebits.co/terms" target="_blank" rel="noreferrer" style={{ color: 'var(--sb-accent)' }}>privacy policy</a>
              </label>
            </div>

            <button type="submit" className="btn btn-primary w-100" style={{ fontWeight: 600, padding: '0.7rem' }}>
              Create account
            </button>
          </form>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '1.5rem 0' }}>
            <div style={{ flex: 1, height: 1, background: 'var(--sb-border)' }} />
            <span style={{ color: 'var(--sb-text-muted)', fontSize: '0.75rem' }}>or</span>
            <div style={{ flex: 1, height: 1, background: 'var(--sb-border)' }} />
          </div>

          <button
            type="button"
            onClick={() => registerInWithGoogle(answers, name)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem',
              padding: '0.7rem 1rem',
              background: 'var(--sb-bg-elevated)', border: '1px solid var(--sb-border-strong)',
              borderRadius: 'var(--sb-radius-sm)', color: 'var(--sb-text-secondary)',
              fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = 'var(--sb-text-primary)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--sb-border-strong)'; e.currentTarget.style.color = 'var(--sb-text-secondary)'; }}
          >
            <i className="fab fa-google" />
            Continue with Google
          </button>

          <p style={{ textAlign: 'center', marginTop: '2rem', fontSize: '0.875rem', color: 'var(--sb-text-muted)' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: 'var(--sb-accent)', fontWeight: 500 }}>Log in</Link>
          </p>
        </div>
      </div>

    </div>
  );
}

export default Register;
